import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Target, Star, Calendar, Trophy, Gift, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';

const KEYBOARD_LAYOUT = [
  { row: 0, keys: ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'] },
  { row: 1, keys: ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'] },
  { row: 2, keys: ['Z', 'X', 'C', 'V', 'B', 'N', 'M'] },
  { row: 3, keys: [' '] }
];

const FINGER_MAP = {
  'Q': 4, 'A': 4, 'Z': 4, 'W': 3, 'S': 3, 'X': 3,
  'E': 2, 'D': 2, 'C': 2, 'R': 1, 'F': 1, 'V': 1, 'T': 1, 'G': 1, 'B': 1,
  'Y': 1, 'H': 1, 'N': 1, 'U': 1, 'J': 1, 'M': 1,
  'I': 2, 'K': 2, 'O': 3, 'L': 3, 'P': 4, ' ': 1
};

const FINGER_COLORS = {
  1: 'bg-red-500/60 border-red-500',
  2: 'bg-yellow-500/60 border-yellow-500',
  3: 'bg-green-500/60 border-green-500',
  4: 'bg-purple-500/60 border-purple-500'
};

const WeeklyChallengePage = () => {
  const { user, refreshUser } = useAuth();
  const inputRef = useRef(null);
  
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [completedPhrases, setCompletedPhrases] = useState([]);
  
  // Typing state
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [keystrokes, setKeystrokes] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [activeKey, setActiveKey] = useState('');
  const [sessionAccuracies, setSessionAccuracies] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchChallenge();
  }, []);

  const fetchChallenge = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/challenges/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(response.data.challenge);
      setLeaderboard(response.data.leaderboard);
    } catch (err) {
      console.error('Failed to fetch challenge:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentPhrase = challenge?.phrases?.[currentPhraseIndex]?.toLowerCase() || '';
  const allPhrasesComplete = currentPhraseIndex >= (challenge?.phrases?.length || 0);

  const resetPhrase = () => {
    setTypedText('');
    setCurrentIndex(0);
    setKeystrokes(0);
    setCorrectKeystrokes(0);
    setStartTime(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Handle virtual keyboard key press
  const handleVirtualKeyPress = (key) => {
    if (allPhrasesComplete) return;
    
    const char = key === ' ' ? ' ' : key.toLowerCase();
    const expectedChar = currentPhrase[currentIndex];
    
    if (!startTime) setStartTime(Date.now());
    
    setActiveKey(key);
    setTimeout(() => setActiveKey(''), 150);
    setKeystrokes(prev => prev + 1);
    
    if (char === expectedChar) {
      setCorrectKeystrokes(prev => prev + 1);
      const newText = typedText + char;
      setTypedText(newText);
      setCurrentIndex(prev => prev + 1);
      
      // Check if phrase is complete
      if (newText.length === currentPhrase.length) {
        completePhraseVirtual(newText);
      }
    }
    
    inputRef.current?.focus();
  };
  
  const completePhraseVirtual = async (finalText) => {
    const accuracy = keystrokes > 0 ? Math.round(((correctKeystrokes + 1) / (keystrokes + 1)) * 100) : 100;
    setSessionAccuracies(prev => [...prev, accuracy]);
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/typing/session`, {
        phrase: currentPhrase,
        typed_text: finalText,
        keystrokes: keystrokes + 1,
        correct_keystrokes: correctKeystrokes + 1,
        duration_seconds: (Date.now() - startTime) / 1000
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (currentPhraseIndex + 1 < challenge?.phrases?.length) {
        setCurrentPhraseIndex(prev => prev + 1);
        resetPhrase();
      } else {
        setShowResults(true);
        await completeChallenge();
      }
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (allPhrasesComplete) return;
    const key = e.key;
    if (key.length === 1 || key === ' ') {
      setActiveKey(key.toUpperCase());
      setTimeout(() => setActiveKey(''), 150);
    }
  }, [allPhrasesComplete]);

  const handleInput = async (e) => {
    if (allPhrasesComplete) return;
    
    const newText = e.target.value.toLowerCase();
    const expectedChar = currentPhrase[currentIndex];
    const typedChar = newText[newText.length - 1];
    
    if (!startTime) setStartTime(Date.now());
    setKeystrokes(prev => prev + 1);
    
    if (typedChar === expectedChar) {
      setCorrectKeystrokes(prev => prev + 1);
      setTypedText(newText);
      setCurrentIndex(prev => prev + 1);
      
      if (newText.length === currentPhrase.length) {
        await completePhrase(newText);
      }
    }
  };

  const completePhrase = async (finalText) => {
    const duration = (Date.now() - startTime) / 1000;
    const accuracy = ((correctKeystrokes + 1) / (keystrokes + 1)) * 100;
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/typing/session`, {
        phrase: currentPhrase,
        typed_text: finalText,
        keystrokes: keystrokes + 1,
        correct_keystrokes: correctKeystrokes + 1,
        duration_seconds: duration,
        is_challenge: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessionAccuracies(prev => [...prev, accuracy]);
      setCompletedPhrases(prev => [...prev, currentPhrase]);
      
      if (currentPhraseIndex + 1 < challenge.phrases.length) {
        setCurrentPhraseIndex(prev => prev + 1);
        resetPhrase();
      } else {
        // All phrases complete!
        setShowResults(true);
        await completeChallenge();
      }
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  };

  const completeChallenge = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/challenges/weekly/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshUser();
      fetchChallenge();
    } catch (err) {
      console.error('Failed to complete challenge:', err);
    }
  };

  const averageAccuracy = sessionAccuracies.length > 0 
    ? sessionAccuracies.reduce((a, b) => a + b, 0) / sessionAccuracies.length 
    : 0;
  const nextKey = currentPhrase[currentIndex]?.toUpperCase() || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <div className="text-bot-cyan animate-pulse font-display text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-space">
      <div className="grid-bg" />
      
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-panel/90 backdrop-blur-md border-b border-mid-panel">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-screen-white/60 hover:text-screen-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Back</span>
          </Link>
          
          <div className="font-display text-xl flex items-center gap-2">
            <Calendar className="w-6 h-6 text-zap-yellow" />
            <span className="text-zap-yellow">WEEKLY CHALLENGE</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-zap-yellow" />
            <span className="font-mono text-sm text-zap-yellow">+{challenge?.bonus_credits || 50}</span>
          </div>
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Challenge Header */}
          <div className="bg-gradient-to-r from-zap-yellow/20 to-bot-cyan/20 border-2 border-zap-yellow/30 rounded-xl p-6 mb-8" data-testid="challenge-header">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-mono text-xs text-zap-yellow mb-1">THIS WEEK'S THEME</div>
                <h1 className="font-display text-3xl text-screen-white">{challenge?.theme || 'Weekly Challenge'}</h1>
              </div>
              <div className="text-right">
                <div className="font-mono text-xs text-screen-white/50">BONUS REWARD</div>
                <div className="font-display text-2xl text-zap-yellow flex items-center gap-2">
                  <Star className="w-6 h-6" />
                  {challenge?.bonus_credits || 50} Coins
                </div>
              </div>
            </div>
            
            {/* Progress */}
            <div className="mt-6">
              <div className="flex justify-between text-xs font-mono text-screen-white/50 mb-2">
                <span>PROGRESS</span>
                <span>{completedPhrases.length}/{challenge?.phrases?.length || 0} PHRASES</span>
              </div>
              <div className="h-3 bg-deep-space rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-zap-yellow to-bot-cyan rounded-full transition-all duration-500"
                  style={{ width: `${(completedPhrases.length / (challenge?.phrases?.length || 1)) * 100}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Typing Area */}
            <div className="lg:col-span-2">
              {!showResults ? (
                <>
                  <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-8 mb-6" data-testid="challenge-phrase">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 bg-zap-yellow rounded-full animate-pulse" />
                      <span className="font-mono text-xs text-zap-yellow">PHRASE {currentPhraseIndex + 1} OF {challenge?.phrases?.length}</span>
                    </div>
                    
                    <div className="font-mono text-2xl md:text-3xl tracking-wide">
                      {currentPhrase.split('').map((char, i) => (
                        <span
                          key={i}
                          className={`${
                            i < currentIndex ? 'text-bot-cyan' :
                            i === currentIndex ? 'bg-zap-yellow/30 text-zap-yellow border-b-2 border-zap-yellow' :
                            'text-screen-white/30'
                          }`}
                        >
                          {char === ' ' ? '\u00A0' : char}
                        </span>
                      ))}
                    </div>
                    
                    <input
                      ref={inputRef}
                      type="text"
                      value={typedText}
                      onChange={handleInput}
                      onKeyDown={handleKeyDown}
                      className="sr-only"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck="false"
                      data-testid="challenge-input"
                    />
                    
                    <button
                      onClick={() => inputRef.current?.focus()}
                      className="w-full mt-4 py-3 border border-mid-panel rounded-lg text-screen-white/50 font-body text-sm hover:border-zap-yellow md:block hidden"
                    >
                      Click here to start typing...
                    </button>
                    
                    <p className="mt-4 text-center text-screen-white/50 text-sm md:hidden">
                      Tap the keys below to type
                    </p>
                  </div>
                  
                  {/* Mini Keyboard - Clickable */}
                  <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-2 sm:p-4">
                    {KEYBOARD_LAYOUT.map((row, rowIndex) => (
                      <div key={rowIndex} className={`flex justify-center gap-0.5 sm:gap-1 mb-0.5 sm:mb-1 ${rowIndex === 1 ? 'ml-2 sm:ml-4' : rowIndex === 2 ? 'ml-4 sm:ml-8' : ''}`}>
                        {row.keys.map((key) => {
                          const finger = FINGER_MAP[key];
                          const isNext = nextKey === key;
                          const isSpace = key === ' ';
                          
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => handleVirtualKeyPress(key)}
                              className={`
                                ${isSpace ? 'w-20 sm:w-32' : 'w-7 sm:w-8'} h-7 sm:h-8 rounded flex items-center justify-center 
                                font-mono text-[10px] sm:text-xs border 
                                ${FINGER_COLORS[finger]} 
                                ${isNext ? 'ring-2 ring-zap-yellow shadow-lg shadow-zap-yellow/30' : ''}
                                hover:brightness-125 active:scale-95 cursor-pointer select-none transition-all
                              `}
                              style={isNext ? { animation: 'gentle-pulse 1.5s ease-in-out infinite' } : {}}
                            >
                              {isSpace ? 'SPC' : key}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* Results */
                <div className="bg-dark-panel border-2 border-zap-yellow rounded-xl p-8 text-center" data-testid="challenge-results">
                  <Trophy className="w-20 h-20 text-zap-yellow mx-auto mb-6" />
                  <h2 className="font-display text-4xl text-screen-white mb-4">CHALLENGE COMPLETE!</h2>
                  
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-deep-space rounded-lg p-4">
                      <Target className="w-8 h-8 text-bot-cyan mx-auto mb-2" />
                      <div className="font-display text-3xl text-bot-cyan">{averageAccuracy.toFixed(1)}%</div>
                      <div className="font-mono text-xs text-screen-white/50">AVG ACCURACY</div>
                    </div>
                    <div className="bg-deep-space rounded-lg p-4">
                      <Star className="w-8 h-8 text-zap-yellow mx-auto mb-2" />
                      <div className="font-display text-3xl text-zap-yellow">+{challenge?.bonus_credits || 50}</div>
                      <div className="font-mono text-xs text-screen-white/50">BONUS COINS</div>
                    </div>
                  </div>
                  
                  <Link
                    to="/dashboard"
                    className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-8 py-3 rounded-sm inline-block"
                  >
                    Back to Dashboard
                  </Link>
                </div>
              )}
            </div>
            
            {/* Leaderboard */}
            <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-6" data-testid="weekly-leaderboard">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-zap-yellow" />
                <h3 className="font-display text-lg text-screen-white">This Week's Leaders</h3>
              </div>
              
              {leaderboard.length === 0 ? (
                <div className="text-center py-8 text-screen-white/50">
                  <p className="font-body text-sm">No completions yet. Be the first!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry, i) => (
                    <div
                      key={entry._id}
                      className={`flex items-center gap-3 p-2 rounded-lg ${entry._id === user?.id ? 'bg-zap-yellow/10' : ''}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-zap-yellow text-deep-space' :
                        i === 1 ? 'bg-gray-300 text-deep-space' :
                        i === 2 ? 'bg-amber-600 text-deep-space' :
                        'bg-mid-panel text-screen-white'
                      }`}>
                        {i + 1}
                      </div>
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-deep-space font-bold text-sm"
                        style={{ backgroundColor: entry.pixel_color }}
                      >
                        {entry.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="font-body text-sm text-screen-white">{entry.username}</div>
                      </div>
                      <div className="font-mono text-sm text-bot-cyan">{entry.avg_accuracy.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WeeklyChallengePage;
